import type { PImage } from '../../engine/aliases'
import type { IDeadish, IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { assert } from '../../lib/assert-sidecar'
import type { IIJ, IXY } from '../../lib/distanceUtil'
import { l } from '../../lib/imports'
import { mathUtils } from '../../lib/mathUtils-sidecar'
import { rando } from '../../lib/rando-sidecar'
import { createId } from '../../main/common'
import { common } from '../../main/common-sidecar'
import { ShaderHighlightPipeline2 } from '../../pipeline/ShaderHighlightPipeline2'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import { chessGameRules } from './chessGameRules-sidecar'
import { chessLayers } from './chessLayers-sidecar'
import { chessParticles } from './chessParticles-sidecar'
import { EChessPieceType, IChessPieceData } from './chessPiecesData'
import { chessPiecesData } from './chessPiecesData-sidecar'
import { chessPiecesUtil } from './chessPiecesUtil-sidecar'
import type { IChessProgressBar } from './chessProgressBars'
import { chessProgressBars } from './chessProgressBars-sidecar'
import { chessTilemap } from './chessTilemap-sidecar'
import type { IChessTilemapPart } from './chessTilemapPart'
import { chessShaderTest } from './tests/chessShaderTest-sidecar'

export enum EChessTeam {
  Default = -1,
  Null = 0,
  White = 1,
  Black = 2,
  Spectator = 3,
}

export interface IChessPiece extends IEntity, IDeadish {
  type: EChessPieceType
  data: IChessPieceData
  team: EChessTeam
  image: PImage
  imageShock: PImage | null
  imageShadow: PImage
  x: number
  y: number
  i: number
  j: number
  isFlipped: boolean
  tile: IChessTilemapPart | null
  energy: number
  bar: IChessProgressBar
}

const _objs: IChessPiece[] = []
export function getAll() {
  return _objs
}

export function findById(id: number) {
  return entitiesUtils.findById(id, _objs)
}

export function findByIdSafe(id: number) {
  return entitiesUtils.findByIdSafe(id, _objs)
}

export function getAt(iij: IIJ, filter?: (f: IChessPiece) => boolean) {
  for (let i = 0; i < _objs.length; i++) {
    let c = _objs[i]
    if (iij.i === c.i && iij.j === c.j) {
      if (!filter || filter(c)) {
        return c
      }
    }
  }
  return null
}

export function create(type: EChessPieceType, team: EChessTeam): IChessPiece {
  let data = chessPiecesData.get(type)
  let frame = data.frames[team === EChessTeam.White ? 0 : 1]

  let image = spritesUtil.createImage(chessLayers.getLayers().pieces, frame)
  image.scale = 0.25

  // let frameShadow =
  //   team === EChessTeam.White
  //     ? sprites.chessShadowWhite[0]
  //     : sprites.chessShadowBlack[0]
  let frameShadow = sprites.chessShadowBlack[0]
  let imageShadow = spritesUtil.createImage(
    chessLayers.getLayers().shadows,
    frameShadow,
  )
  imageShadow.scale = 0.75
  imageShadow.alpha = 0.5

  if (team === EChessTeam.White) {
    image.flipX = true
  }

  let bar = chessProgressBars.create()

  let id = createId()

  let obj: IChessPiece = {
    id,
    idLoaded: -1,
    isDead: false,
    eType: 'voy-misc',
    image,
    imageShadow,
    imageShock: null,
    type,
    data,
    x: 0,
    y: 0,

    i: -1,
    j: -1,

    team,
    isFlipped: false,
    tile: null,
    energy: 0,
    bar,
  }
  _objs.push(obj)

  if (type === EChessPieceType.King) {
    // || type === EChessPieceType.Pawn) {
    let imageShock = spritesUtil.createImage(
      chessLayers.getLayers().abovePieces,
      sprites.chessShock[0],
    )
    imageShock.scale = 0.1
    obj.imageShock = imageShock
  }

  return obj
}

export function moveToIJ(obj: IChessPiece, iij: IIJ, resetEnergy = true) {
  let toI = iij.i
  let toJ = iij.j
  assert.f(obj.isDead, 'piece is dead, cannot move!')
  _removeFromTile(obj)

  obj.i = toI
  obj.j = toJ
  let xy = chessTilemap.convertIJtoXY(obj.i, obj.j)
  moveToXY(obj, xy.x, xy.y)

  obj.imageShadow.x = xy.x
  obj.imageShadow.y = xy.y + 32

  let t = chessTilemap.getSafeIJ(iij)
  if (t) {
    t.pieces.push(obj)
  }
  obj.tile = t
  if (resetEnergy) {
    obj.energy = 0
  }
}

export function moveToXY(obj: IChessPiece, x: number, y: number) {
  obj.x = x
  obj.y = y
  obj.image.x = x + obj.data.ox
  obj.image.y = y + obj.data.oy

  if (obj.imageShock) {
    let dir = obj.team === EChessTeam.Black ? 1 : -1
    obj.imageShock.x = x + obj.data.ox + dir * 5
    obj.imageShock.y = y + obj.data.oy - 44
    obj.imageShock.flipX = obj.team === EChessTeam.Black
  }

  chessProgressBars.moveTo(obj.bar, x, y + 64 - 16)
}

function _removeFromTile(obj: IChessPiece) {
  if (obj.tile) {
    l.remove(obj.tile.pieces, obj)
    obj.tile = null
    obj.i = -1
    obj.j = -1
  }
}

export function kill(obj: IChessPiece) {
  obj.isDead = true
  _removeFromTile(obj)
  chessProgressBars.kill(obj.bar)
}
export function killAll() {
  l.forEach(_objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(_objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(_objs, (c) => {
    c.image.destroy()
    c.imageShadow.destroy()
    if (c.imageShock) {
      c.imageShock.destroy()
    }
  })
}
export function update(obj: IChessPiece) {
  let ds = common.getUpdateDeltaScaled()
  obj.energy += ds

  let moveTime = getMoveTime(obj)
  let v = obj.energy / moveTime

  if (obj.imageShock) {
    obj.imageShock.alpha = mathUtils.sineLike(
      common.getElapsedTime(),
      7,
      0.5,
      1,
    )
    obj.imageShock.scaleX =
      0.1 * mathUtils.sineLike(common.getElapsedTime(), 3, 0.9, 1.1)
    obj.imageShock.scaleY =
      0.1 * mathUtils.sineLike(common.getElapsedTime(), 5, 0.9, 1.1)

    // See if any piece can get us
    obj.imageShock.visible = chessPiecesUtil.isInCheck(obj)
  }

  if (v < 1) {
    obj.image.alpha = 0.8
    obj.image.tint = 0xcccccc
  } else {
    obj.image.alpha = 1
    obj.image.tint = 0xffffff
  }

  chessProgressBars.setValue(obj.bar, v)
  chessProgressBars.setVisible(obj.bar, v < 1)
}

export function getMoveTime(obj: IChessPiece) {
  return chessGameRules.get().moveTime
}
export function readyToMove(obj: IChessPiece) {
  return obj.energy >= getMoveTime(obj)
}

export function createDust(obj: IChessPiece) {
  chessParticles.createMultiple('dust', obj.x, obj.y + 20, rando.i(1, 5))
}

export function createRocks(obj: IChessPiece, isKill: boolean) {
  let bonus = 0
  if (isKill) {
    bonus += 5
    if (obj.type === EChessPieceType.King) {
      bonus += 20
    } else {
      bonus += obj.data.pointCost
    }
  }

  chessParticles.createMultiple(
    obj.team === EChessTeam.White ? 'rock-white' : 'rock-black',
    obj.x,
    obj.y + 26,
    rando.i(2, 5) + bonus,
  )
}

export function moveExternalShadowTo(obj: IChessPiece, img: PImage) {
  let xy = chessTilemap.convertIJtoXY(obj.i, obj.j)
  img.x = xy.x
  img.y = xy.y + 32
  img.visible = true
}

export function moveExternalShadow2To(img: PImage, target: IXY) {
  let mij = chessTilemap.convertXYtoIJ(target)
  // let mxy = chessTilemap.convertIJtoXY(mij.i, mij.j)
  let tHere = chessTilemap.getSafe(mij.i, mij.j)
  if (tHere) {
    img.x = tHere.x
    img.y = tHere.y + 32
    img.visible = true
  } else {
    img.visible = false
  }
}

export function setHighlighted(obj: IChessPiece, isHighlighted: boolean) {
  if (isHighlighted) {
    let pipeline = chessShaderTest.getShader(ShaderHighlightPipeline2.KEY)
    pipeline.set2fv('uUvCenter', [
      (obj.image.frame.u0 + obj.image.frame.u1) / 2.0,
      (obj.image.frame.v0 + obj.image.frame.v1) / 2.0,
    ])
    obj.image.setPipeline(pipeline)
    // obj.image.setPipelineData(
    //   'uvcu',
    //   (obj.image.frame.u0 + obj.imageShadow.frame.u1) / 2.0,
    // )
    // obj.image.setPipelineData(
    //   'uvcv',
    //   (obj.image.frame.v0 + obj.imageShadow.frame.v1) / 2.0,
    // )
  } else {
    obj.image.resetPipeline()
  }
}
